// =============================================================================
// BP-08-05: Vendor Portal Frontend - useVendorOrders Hook
// =============================================================================

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { vendorOrdersApi } from '@/features/vendor/api/vendorApi';
import type { 
  VendorOrder, 
  OrderListParams, 
  OrderStatusUpdate 
} from '@/features/vendor/types/vendorTypes';

// Query keys
export const vendorOrderKeys = {
  all: ['vendor-orders'] as const,
  lists: () => [...vendorOrderKeys.all, 'list'] as const,
  list: (params: OrderListParams) => [...vendorOrderKeys.lists(), params] as const,
  details: () => [...vendorOrderKeys.all, 'detail'] as const,
  detail: (id: string) => [...vendorOrderKeys.details(), id] as const,
  pending: () => [...vendorOrderKeys.all, 'pending'] as const,
};

/**
 * Hook for fetching vendor orders list
 */
export function useVendorOrders(params: OrderListParams = {}) {
  return useQuery({
    queryKey: vendorOrderKeys.list(params),
    queryFn: () => vendorOrdersApi.list(params),
    staleTime: 60 * 1000, // 1 minute
    refetchInterval: 2 * 60 * 1000, // Refetch every 2 minutes for new orders
  });
}

/**
 * Hook for fetching pending orders (quick count for dashboard)
 */
export function useVendorPendingOrders() {
  return useQuery({
    queryKey: vendorOrderKeys.pending(),
    queryFn: () => vendorOrdersApi.list({ status: 'pending', pageSize: 5 }),
    staleTime: 30 * 1000, // 30 seconds
    refetchInterval: 60 * 1000, // Refetch every minute
  });
}

/**
 * Hook for fetching single order
 */
export function useVendorOrder(orderId: string | undefined) {
  return useQuery({
    queryKey: vendorOrderKeys.detail(orderId!),
    queryFn: () => vendorOrdersApi.get(orderId!),
    enabled: !!orderId,
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
}

/**
 * Hook for updating order status
 */
export function useUpdateOrderStatus() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ orderId, update }: { orderId: string; update: OrderStatusUpdate }) =>
      vendorOrdersApi.updateStatus(orderId, update),
    onSuccess: (updatedOrder) => {
      // Update the specific order in cache
      queryClient.setQueryData<VendorOrder>(
        vendorOrderKeys.detail(updatedOrder.id),
        updatedOrder
      );
      // Invalidate lists to refresh counts
      queryClient.invalidateQueries({ queryKey: vendorOrderKeys.lists() });
      queryClient.invalidateQueries({ queryKey: vendorOrderKeys.pending() });
    },
  });
}

/**
 * Hook for confirming an order
 */
export function useConfirmOrder() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (orderId: string) => vendorOrdersApi.confirm(orderId),
    onSuccess: (updatedOrder) => {
      queryClient.setQueryData<VendorOrder>(
        vendorOrderKeys.detail(updatedOrder.id),
        updatedOrder
      );
      queryClient.invalidateQueries({ queryKey: vendorOrderKeys.lists() });
      queryClient.invalidateQueries({ queryKey: vendorOrderKeys.pending() });
    },
  });
}

/**
 * Hook for shipping an order
 */
export function useShipOrder() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ orderId, trackingNumber }: { orderId: string; trackingNumber: string }) =>
      vendorOrdersApi.ship(orderId, trackingNumber),
    onSuccess: (updatedOrder) => {
      queryClient.setQueryData<VendorOrder>(
        vendorOrderKeys.detail(updatedOrder.id),
        updatedOrder
      );
      queryClient.invalidateQueries({ queryKey: vendorOrderKeys.lists() });
    },
  });
}

/**
 * Hook for marking order as delivered
 */
export function useDeliverOrder() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (orderId: string) => vendorOrdersApi.deliver(orderId),
    onSuccess: (updatedOrder) => {
      queryClient.setQueryData<VendorOrder>(
        vendorOrderKeys.detail(updatedOrder.id),
        updatedOrder
      );
      queryClient.invalidateQueries({ queryKey: vendorOrderKeys.lists() });
    },
  });
}

/**
 * Hook for cancelling an order
 */
export function useCancelOrder() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ orderId, reason }: { orderId: string; reason: string }) =>
      vendorOrdersApi.cancel(orderId, reason),
    onSuccess: (updatedOrder) => {
      queryClient.setQueryData<VendorOrder>(
        vendorOrderKeys.detail(updatedOrder.id),
        updatedOrder
      );
      queryClient.invalidateQueries({ queryKey: vendorOrderKeys.lists() });
      queryClient.invalidateQueries({ queryKey: vendorOrderKeys.pending() });
    },
  });
}

/**
 * Hook for order actions based on current status
 */
export function useOrderActions(order: VendorOrder | undefined) {
  const confirmMutation = useConfirmOrder();
  const shipMutation = useShipOrder();
  const deliverMutation = useDeliverOrder();
  const cancelMutation = useCancelOrder();

  if (!order) {
    return {
      canConfirm: false,
      canShip: false,
      canDeliver: false,
      canCancel: false,
      confirm: () => {},
      ship: (_: string) => {},
      deliver: () => {},
      cancel: (_: string) => {},
      isLoading: false,
    };
  }

  return {
    canConfirm: order.status === 'pending',
    canShip: order.status === 'confirmed' || order.status === 'processing',
    canDeliver: order.status === 'shipped',
    canCancel: ['pending', 'confirmed', 'processing'].includes(order.status),
    confirm: () => confirmMutation.mutate(order.id),
    ship: (trackingNumber: string) => shipMutation.mutate({ orderId: order.id, trackingNumber }),
    deliver: () => deliverMutation.mutate(order.id),
    cancel: (reason: string) => cancelMutation.mutate({ orderId: order.id, reason }),
    isLoading:
      confirmMutation.isPending ||
      shipMutation.isPending ||
      deliverMutation.isPending ||
      cancelMutation.isPending,
  };
}

